import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useData } from "../context/DataContext";
import { useAuth } from "../context/AuthContext";
import { projectRawTotal, projectWeightedTotal } from "../lib/calc";
import { projectProfitability, marginColor } from "../lib/profitability";
import { ratesFromData, resolvedPeople } from "../lib/personnel";
import { fmtMoney, contractLabel, projectNumber } from "../lib/format";
import { downloadCsv } from "../lib/csv";
import ProjectDrawer from "./ProjectDrawer";
import InfoTip from "./InfoTip";
import DeleteProjectButton from "./DeleteProjectButton";
import { ArrowUpDown, Search, Download } from "lucide-react";
import DragScrollContainer from "./DragScrollContainer";

export const AllProjectsTable = () => {
  const { data, rawData, refresh } = useData();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [sortKey, setSortKey] = useState("raw");
  const [sortDir, setSortDir] = useState("desc");
  const [principalId, setPrincipalId] = useState(searchParams.get("principal") || "all");
  const [contract, setContract] = useState("all");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);

  // Keep URL in sync (?principal=...) — other params (e.g. ?view=all) preserved.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (principalId === "all") next.delete("principal");
    else next.set("principal", principalId);
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [principalId]);

  // React when the "View in All Projects" card button changes ?principal=.
  useEffect(() => {
    const fromUrl = searchParams.get("principal") || "all";
    setPrincipalId((curr) => (curr === fromUrl ? curr : fromUrl));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const principalsMap = Object.fromEntries(data.principals.map((p) => [p.id, p.name]));

  // Margin % per project — computed on RAW project data (scenario baking
  // must not distort profitability) with server-computed tier rates.
  const marginById = useMemo(() => {
    const rates = ratesFromData(rawData);
    const people = resolvedPeople(rawData);
    return Object.fromEntries(
      (rawData?.projects || []).map((p) => [p.id, projectProfitability(p, rates, people).marginPct])
    );
  }, [rawData]);

  const rows = useMemo(() => {
    const enriched = data.projects.map((p) => ({
      ...p,
      raw: projectRawTotal(p),
      weighted: projectWeightedTotal(p),
      stages: p.scopes.length,
      principalName: principalsMap[p.principalId] || "—",
      marginPct: marginById[p.id] ?? null,
    }));
    let out = enriched;
    if (principalId !== "all") out = out.filter((r) => r.principalId === principalId);
    if (contract !== "all") out = out.filter((r) => r.contract === contract);
    if (query.trim()) {
      const q = query.toLowerCase();
      out = out.filter((r) => r.name.toLowerCase().includes(q) || r.id.toLowerCase().includes(q));
    }
    out.sort((a, b) => {
      let av = a[sortKey];
      let bv = b[sortKey];
      if (typeof av === "string") return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      if (av == null) av = -Infinity;
      if (bv == null) bv = -Infinity;
      return sortDir === "asc" ? av - bv : bv - av;
    });
    return out;
  }, [data, principalId, contract, query, sortKey, sortDir, principalsMap, marginById]);

  const setSort = (k) => {
    if (k === sortKey) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(k); setSortDir(k === "name" || k === "principalName" || k === "contract" ? "asc" : "desc"); }
  };

  const totalRaw = rows.reduce((s, r) => s + r.raw, 0);
  const totalWtd = rows.reduce((s, r) => s + r.weighted, 0);

  const exportCsv = () => {
    const header = ["Project", "ID", "Principal", "Contract", "Stages", "Value", "Weighted", "Margin %"];
    const body = rows.map((r) => [r.name, r.id, r.principalName, r.contract, r.stages, r.raw, r.weighted,
      r.marginPct == null ? "" : (r.marginPct * 100).toFixed(1)]);
    downloadCsv("projects.csv", [header, ...body]);
  };

  return (
    <div className="space-y-8" data-testid="projects-view">
      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center border border-[var(--rule)] p-3">
        <div className="flex items-center gap-2 px-2 border border-[var(--rule)]">
          <Search size={12} className="text-[var(--soft-fg)]" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search project"
            className="py-1.5 bg-transparent outline-none text-sm font-mono-num"
            data-testid="projects-search"
          />
        </div>

        <Select label="Principal" value={principalId} onChange={setPrincipalId} testid="filter-principal"
          options={[{value:"all", label:"All principals"}, ...data.principals.map((p) => ({value:p.id, label:p.name}))]} />

        <Select label="Contract" value={contract} onChange={setContract} testid="filter-contract"
          options={[{value:"all", label:"All"}, {value:"Yes", label:"Confirmed"}, {value:"Tentative", label:"Tentative"}, {value:"No", label:"No (legacy)"}]} />

        <div className="ml-auto eyebrow">
          Showing <span className="text-[var(--ink)] font-mono-num">{rows.length}</span> of {data.projects.length}
        </div>
        <div className="eyebrow border-l border-[var(--rule)] pl-3">
          Total <span className="text-[var(--ink)] font-mono-num" data-testid="projects-filtered-total">{fmtMoney(totalRaw, { compact: true })}</span>
          <span className="text-[var(--soft-fg)] font-mono-num ml-1.5">{fmtMoney(totalWtd, { compact: true })}</span>
        </div>
        <button
          onClick={exportCsv}
          className="inline-flex items-center gap-2 px-3 py-1.5 border border-[var(--rule-strong)] text-xs font-display tracked-tight uppercase hover:bg-[var(--paper-soft)]"
          data-testid="projects-export-csv"
        >
          <Download size={12} /> CSV
        </button>
      </div>

      {/* Table */}
      <DragScrollContainer className="border border-[var(--rule)] overflow-x-auto thin-scroll">
        <table className="w-full text-sm">
          <thead>
            <tr className="eyebrow">
              {user?.role === "admin" && <th className="w-16 border-r border-[var(--rule)]" />}
              <Th onClick={() => setSort("name")} active={sortKey==="name"}>Project</Th>
              <Th onClick={() => setSort("principalName")} active={sortKey==="principalName"}>Principal</Th>
              <Th onClick={() => setSort("contract")} active={sortKey==="contract"}>Contract</Th>
              <Th onClick={() => setSort("stages")} active={sortKey==="stages"} right>Stages</Th>
              <Th onClick={() => setSort("raw")} active={sortKey==="raw"} right>Raw total</Th>
              <Th onClick={() => setSort("weighted")} active={sortKey==="weighted"} right>
                <span className="inline-flex items-center">
                  Weighted
                  <InfoTip side="left" label="Weighted total">
                    Raw total × each scope's probability. So a $100k project at 70% probability counts as $70k. Useful for forecasting when not every project will land.
                  </InfoTip>
                </span>
              </Th>
              <Th onClick={() => setSort("marginPct")} active={sortKey==="marginPct"} right>
                <span className="inline-flex items-center">
                  Margin %
                  <InfoTip side="left" label="Margin %">
                    (Fee − personnel cost) ÷ fee, from the project form's profitability calculator. Green ≥ 15%, amber 0–15%, red when personnel cost exceeds the fee. "—" means no resourcing has been planned yet.
                  </InfoTip>
                </span>
              </Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const chip = r.contract === "Yes" ? "chip-yes" : r.contract === "Tentative" ? "chip-tentative" : "chip-no";
              return (
                <tr
                  key={r.id}
                  className="border-t border-[var(--rule)] hover:bg-[var(--paper-soft)] cursor-pointer"
                  onClick={() => setSelected(r)}
                  data-testid={`project-row-${r.id}`}
                >
                  {/* Delete at the FAR LEFT, away from the row's open/edit
                      affordance — muted until hover/focus, 44x44 target. */}
                  {user?.role === "admin" && (
                    <td className="px-2 py-3 text-center border-r border-[var(--rule)]">
                      <DeleteProjectButton
                        project={(rawData?.projects || []).find((p) => p.id === r.id)}
                        isAdmin
                        onDeleted={refresh}
                      />
                    </td>
                  )}
                  <td className="px-4 py-3">
                    <div className="text-[var(--ink)]">{r.name}</div>
                    <div className="font-mono-num text-[10px] text-[var(--soft-fg)] mt-0.5">{projectNumber(r)}</div>
                    {r.lastEditedAt && (
                      <div className="text-[10px] text-[var(--soft-fg)] mt-0.5" data-testid={`projects-last-edited-${r.id}`}>
                        Edited {new Date(r.lastEditedAt).toLocaleDateString("en-NZ", { day: "2-digit", month: "short" })}
                        {r.lastEditedBy ? ` · ${String(r.lastEditedBy).split("@")[0]}` : ""}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">{r.principalName}</td>
                  <td className="px-4 py-3"><span className={`chip ${chip}`}>{contractLabel(r.contract)}</span></td>
                  <td className="px-4 py-3 text-right font-mono-num">{r.stages}</td>
                  <td className="px-4 py-3 text-right font-mono-num">{fmtMoney(r.raw)}</td>
                  <td className="px-4 py-3 text-right font-mono-num text-[var(--soft-fg)]">{fmtMoney(r.weighted)}</td>
                  <td className="px-4 py-3 text-right font-mono-num" data-testid={`project-margin-${r.id}`}>
                    {r.marginPct == null || r.raw <= 0 ? (
                      <span className="text-[var(--soft-fg)]">—</span>
                    ) : (
                      <span
                        className="inline-flex items-center gap-1.5"
                        style={{ color: marginColor(r.marginPct) }}
                        title={r.marginPct < 0 ? "Loss-making — personnel cost exceeds fee" : r.marginPct < 0.15 ? "Below 15% margin target" : "On target — margin ≥ 15%"}
                      >
                        <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: marginColor(r.marginPct) }} />
                        {(r.marginPct * 100).toFixed(1)}%
                      </span>
                    )}
                  </td>
                  {/* Delete moved to the far-left column of this row. */}
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr>
                <td colSpan={8} className="text-center py-12">
                  <div className="eyebrow text-[var(--soft-fg)]">No projects match these filters</div>
                  {(principalId !== "all" || contract !== "all" || query.trim()) ? (
                    <button
                      onClick={() => { setPrincipalId("all"); setContract("all"); setQuery(""); }}
                      className="mt-3 px-3 py-1.5 border border-[var(--rule-strong)] text-xs font-display tracked-tight uppercase hover:bg-[var(--paper-soft)]"
                      data-testid="projects-clear-filters"
                    >
                      Clear filters
                    </button>
                  ) : (
                    <div className="text-xs font-mono-num text-[var(--soft-fg)] mt-2">
                      Projects are added by principals from their portfolio, or by admins via My Projects.
                    </div>
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </DragScrollContainer>

      <ProjectDrawer project={selected} onClose={() => setSelected(null)} />
    </div>
  );
};

function Th({ children, onClick, active, right }) {
  return (
    <th
      onClick={onClick}
      className={`px-4 py-3 cursor-pointer select-none ${right ? "text-right" : "text-left"} ${active ? "text-[var(--ink)]" : ""}`}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        <ArrowUpDown size={10} className="opacity-50" />
      </span>
    </th>
  );
}

function Select({ label, value, onChange, options, testid }) {
  return (
    <label className="inline-flex items-center gap-2 border border-[var(--rule)] px-3 py-1.5">
      <span className="eyebrow">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-transparent text-sm outline-none font-mono-num"
        data-testid={testid}
      >
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}
